import { helpMenuContent, helpMenuHeader, version } from "./HelpmenuData";
import { DragResizeModal } from "../ContextMenu/Modals/DragResizeModal";
import debug from "debug";
const log = debug("app:helpmenu");

/**
 * Class to manage the Help Menu functionality, including creating, toggling, and handling a draggable modal.
 * @category Components
 * @subcategory HelpMenu
 */
export class HelpMenu {
  /**
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.eventHub = experience.eventHub;
    this.modal = null;
    this.triggerButton = null;
    this.modalOpen = false; // Track the state of the modal
    document.addEventListener("closeModal", this.hideModal.bind(this));
    this.init();
  }

  /**
   * Initializes the Help Menu by setting up event listeners.
   */
  init() {
    this.eventHub.on("helpmenu", this.toggleMenu.bind(this));
  }

  cleanup() {
    console.log("HelpMenu cleanup started...");

    // 1. Remove event listeners from eventHub
    if (this.eventHub) {
      this.eventHub.off("helpmenu", this.toggleMenu);
    }
  
    // 2. Remove global document event listener
    document.removeEventListener("closeModal", this.hideModal);
  
    // 3. Remove modal from the DOM
    if (this.modal && this.modal.parentNode) {
      this.modal.remove();
      this.modal = null;
    }
  
    // 4. Reset trigger button styles
    if (this.triggerButton) {
      this.triggerButton.classList.remove("bold-icon");
    }
  
    // 5. Nullify object references
    this.eventHub = null;
    this.triggerButton = null;
  
    console.log("HelpMenu cleanup completed.");
  }

  /**
   * Toggles the Help Menu modal.
   * @param {Event} [event] - The triggering event, used to determine the button triggering the menu.
   */
  toggleMenu(event) {
    this.triggerButton = event ? event.currentTarget : null;

    if (this.modalOpen) {
      this.hideModal();
    } else {
      this.createHelpMenuModal();
    }
  }

  /**
   * Creates the Help Menu modal and appends it to the document body.
   * If the modal already exists, it makes it visible.
   */
  createHelpMenuModal() {
    if (!this.modal) {
      this.modal = document.createElement("div");
      this.modal.classList.add("help-modal-container");

      // Create modal header with version info and close button
      const modalHeader = document.createElement("div");
      modalHeader.classList.add("modal-top-header");

      modalHeader.innerHTML = `${helpMenuHeader} ${version}`;

      const closeButton = document.createElement("img");
      closeButton.classList.add("close-button-modal");
      closeButton.setAttribute("src", "./imgs/Close.png");
      closeButton.addEventListener("click", () => this.hideModal());

      modalHeader.appendChild(closeButton);

      // Create modal content with help information
      const modalContent = document.createElement("div");
      modalContent.classList.add("help-modal-content");

      modalContent.innerHTML = helpMenuContent;

      this.modal.appendChild(modalHeader);
      this.modal.appendChild(modalContent);

      document.body.appendChild(this.modal);

      // Make the modal draggable by the header
      new DragResizeModal(this.modal, this.modal);
    } else {
      this.modal.style.display = "block";
    }

    // Highlight the trigger button if available
    if (this.triggerButton) {
      this.triggerButton.classList.add("bold-icon");
    }

    log("Event: modalisOpened");
    this.eventHub.emit("lockEvents", true);
    this.modalOpen = true; // Set modal state to open
  }

  /**
   * Hides and removes the Help Menu modal from the DOM.
   */
  hideModal() {
    if (this.modal && this.modalOpen) {
      // Remove modal from DOM
      this.modal.remove();
      this.modal = null; // Reset modal reference
      this.modalOpen = false; // Set modal state to closed

      // Reset events or styles
      log("Event: modalIsClosed");
      this.eventHub.emit("lockEvents", false);
    }

    // Reset trigger button styles if any
    if (this.triggerButton) {
      this.triggerButton.classList.remove("bold-icon");
    }
  }

  /**
   * Enables dragging of the modal by attaching mouse events to a draggable handle.
   * @param {HTMLElement} draggableHandle - The element used to drag the modal.
   * @param {HTMLElement} draggableContainer - The modal container to be moved.
   */
  enableDragging(draggableHandle, draggableContainer) {
    let isDragging = false;
    let pointerStartX, pointerStartY, modalStartX, modalStartY;

    draggableHandle.style.cursor = "grab";

    draggableHandle.addEventListener("mousedown", (e) => {
      e.preventDefault();
      isDragging = true;

      draggableHandle.style.cursor = "grabbing";

      // Get the current position without overriding resize behavior
      const rect = draggableContainer.getBoundingClientRect();
      pointerStartX = e.clientX;
      pointerStartY = e.clientY;

      modalStartX = rect.left;
      modalStartY = rect.top;

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });

    const onMouseMove = (e) => {
      if (!isDragging) return;

      // Update modal position while maintaining its current size
      const deltaX = pointerStartX - e.clientX;
      const deltaY = pointerStartY - e.clientY;

      draggableContainer.style.left = `${modalStartX - deltaX}px`;
      draggableContainer.style.top = `${modalStartY - deltaY}px`;
      draggableContainer.style.position = "fixed"; // Ensure position stays fixed
    };

    const onMouseUp = () => {
      isDragging = false;
      draggableHandle.style.cursor = "grab";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }
}
